import { useEffect, useMemo, useState } from 'react'
import { SYMPTOM_KEYS, type Flow, type Protection } from '../../shared/types'
import { useI18n } from '../i18n'
import { useStore } from '../store'
import { Button, Chip, Sheet, Spinner } from './ui'

const FLOWS: Flow[] = ['spot', 'light', 'medium', 'heavy']
const PROTECTIONS: Protection[] = ['none', 'condom', 'other']

/**
 * 记录面板。目标是 10 秒内记完今天 —— 全部是点选，只有备注需要打字。
 * 打开时会把这一天已有的记录读出来，所以「新增」和「修改」是同一个界面。
 */
export function LogSheet({
  date,
  open,
  onClose,
}: {
  date: string
  open: boolean
  onClose: () => void
}) {
  const { t, formatDate } = useI18n()
  const { snapshot, saveDay } = useStore()

  const existing = useMemo(() => {
    const period = snapshot?.periods.find((p) => p.date === date) ?? null
    const symptom = snapshot?.symptoms.find((s) => s.date === date) ?? null
    const intimacy = snapshot?.intimacy.find((i) => i.date === date) ?? null
    return { period, symptom, intimacy }
  }, [snapshot, date])

  const [flow, setFlow] = useState<Flow | null>(null)
  const [tags, setTags] = useState<string[]>([])
  const [mood, setMood] = useState<number | null>(null)
  const [energy, setEnergy] = useState<number | null>(null)
  const [intimacy, setIntimacy] = useState<Protection | null>(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 每次打开都用当天的实际记录重置表单，避免上次编辑的残留串到别的日子
  useEffect(() => {
    if (!open) return
    setFlow(existing.period?.flow ?? null)
    setTags(existing.symptom?.symptoms ?? [])
    setMood(existing.symptom?.mood ?? null)
    setEnergy(existing.symptom?.energy ?? null)
    setIntimacy(existing.intimacy?.protection ?? null)
    setNote(existing.symptom?.note ?? '')
    setError(null)
  }, [open, date, existing])

  const toggleTag = (k: string) =>
    setTags((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveDay(date, {
        period: flow ? { flow } : null,
        symptom:
          tags.length || mood !== null || energy !== null || note.trim()
            ? { symptoms: tags, mood, energy, note: note.trim() || null }
            : null,
        intimacy: intimacy ? { protection: intimacy } : null,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error)
    } finally {
      setSaving(false)
    }
  }

  const hasAnything =
    flow !== null || tags.length > 0 || mood !== null || energy !== null || intimacy !== null

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.log.title(formatDate(date))}
      footer={
        <div className="space-y-2">
          {error && <p className="text-sm text-period text-center">{error}</p>}
          <Button className="w-full" onClick={submit} disabled={saving}>
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <Spinner /> {t.log.saving}
              </span>
            ) : (
              t.log.save
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 pb-2">
        <Field label={t.log.flow}>
          <div className="flex flex-wrap gap-2">
            <Chip active={flow === null} onClick={() => setFlow(null)}>
              {t.log.flowNone}
            </Chip>
            {FLOWS.map((f) => (
              <Chip key={f} active={flow === f} tone="period" onClick={() => setFlow(f)}>
                {t.flow[f]}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label={t.log.symptoms}>
          <div className="flex flex-wrap gap-2">
            {SYMPTOM_KEYS.map((k) => (
              <Chip key={k} active={tags.includes(k)} onClick={() => toggleTag(k)}>
                {t.symptom[k]}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label={t.log.mood}>
          <Scale
            value={mood}
            labels={t.mood}
            // 心情用 -2..2，0 是「还行」
            toValue={(i) => i - 2}
            fromValue={(v) => v + 2}
            onChange={setMood}
          />
        </Field>

        <Field label={t.log.energy}>
          <Scale
            value={energy}
            labels={t.energy}
            toValue={(i) => i + 1}
            fromValue={(v) => v - 1}
            onChange={setEnergy}
          />
        </Field>

        <Field label={t.log.intimacy}>
          <div className="flex flex-wrap gap-2">
            <Chip active={intimacy === null} onClick={() => setIntimacy(null)}>
              {t.log.intimacyNone}
            </Chip>
            {PROTECTIONS.map((p) => (
              <Chip
                key={p}
                active={intimacy === p}
                tone="fertile"
                onClick={() => setIntimacy(p)}
              >
                {t.protection[p]}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label={t.log.note}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t.log.notePlaceholder}
            rows={3}
            className="w-full rounded-2xl bg-bg-soft border border-transparent focus:border-line px-4 py-3 text-[15px] outline-none resize-none"
          />
        </Field>

        {hasAnything && (
          <button
            onClick={() => {
              setFlow(null)
              setTags([])
              setMood(null)
              setEnergy(null)
              setIntimacy(null)
              setNote('')
            }}
            className="text-sm text-muted underline underline-offset-4 pb-2"
          >
            {t.log.clear}
          </button>
        )}
      </div>
    </Sheet>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-muted mb-3">{label}</h3>
      {children}
    </div>
  )
}

/** 五档选择。再次点已选中的那档就取消选择，不用额外放一个「清除」按钮。 */
function Scale({
  value,
  labels,
  toValue,
  fromValue,
  onChange,
}: {
  value: number | null
  labels: string[]
  toValue: (index: number) => number
  fromValue: (value: number) => number
  onChange: (v: number | null) => void
}) {
  const selectedIndex = value === null ? -1 : fromValue(value)
  return (
    <div className="flex gap-2">
      {labels.map((label, i) => {
        const active = selectedIndex === i
        return (
          <button
            key={label}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? null : toValue(i))}
            className={`flex-1 min-h-[52px] rounded-2xl text-xs leading-tight transition active:scale-95 px-1 ${
              active ? 'bg-ink text-bg' : 'bg-bg-soft text-ink/70'
            }`}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
